'use client';

import { isEmpty } from 'lodash';
import { Check, ChevronDown, ChevronRight } from 'lucide-react';
import React, { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';

import { useControl } from 'src/hooks/use-control';
import { cn } from 'src/lib/utils';

import type { RenderNodeItemProps, TreeNodeData } from './types';
import {
  getAllKeys,
  getAllLeafKeys,
  getExpandedKeysForSelection,
  getNodeCheckState,
} from './utils';

// Tree 组件 ref 类型
export interface TreeRef {
  setExpandedKeys: (keys: string[]) => void;
  getExpandedKeys: () => string[];
  expandAll: () => void;
  collapseAll: () => void;
  resetExpand: () => void;
}

// Tree 组件的 props 类型
export interface TreeProps<TData = unknown, TMultiple extends boolean = false> {
  className?: string;
  nodeClassName?: string;
  expandIcons?: React.ReactNode[];
  data: TreeNodeData<TData>[];
  multiple?: TMultiple;
  selection?: TMultiple extends true ? string[] : string | null;
  onSelectionChange?: (selection: TMultiple extends true ? string[] : string | null) => void;
  expandedKeys?: string[];
  onExpandedChange?: (expandedKeys: string[]) => void;
  showCheckbox?: boolean;
  showSelectedIcon?: boolean;
  defaultExpandAll?: boolean;
  defaultExpandedKeys?: string[];
  defaultSelection?: TMultiple extends true ? string[] : string;
  onNodeClick?: (node: TreeNodeData<TData>, event: React.MouseEvent) => void;
  renderNodeItem?: (props: RenderNodeItemProps<TData>) => React.ReactNode;
  emptyText?: string;
  /** 判断节点是否可选，返回 false 时点击节点将展开/收起而非选中 */
  isNodeSelectable?: (node: TreeNodeData<TData>) => boolean;
}

// 树节点组件
function TreeNode<TData = unknown, TMultiple extends boolean = false>({
  className,
  expandIcons,
  node,
  level,
  selection,
  expandedKeys,
  multiple,
  showCheckbox,
  showSelectedIcon = false,
  onToggleExpand,
  onToggleSelect,
  onNodeClick,
  renderNodeItem,
  isNodeSelectable,
}: {
  className?: string;
  expandIcons?: React.ReactNode[];
  node: TreeNodeData<TData>;
  level: number;
  selection?: TMultiple extends true ? string[] : string | null;
  expandedKeys: string[];
  multiple?: TMultiple;
  showCheckbox: boolean;
  showSelectedIcon?: boolean;
  onToggleExpand: (key: string) => void;
  onToggleSelect: (key: string) => void;
  onNodeClick?: (node: TreeNodeData<TData>, event: React.MouseEvent) => void;
  renderNodeItem?: (props: RenderNodeItemProps<TData>) => React.ReactNode;
  isNodeSelectable?: (node: TreeNodeData<TData>) => boolean;
}) {
  // 判断是否有子节点：优先使用 isLeaf（不受搜索过滤影响），否则检查 children
  const hasChildren =
    node.isLeaf === false ||
    (node.isLeaf === undefined && node.children && node.children.length > 0);
  const isExpanded = expandedKeys.includes(node.key);

  // 计算节点的选中状态（支持半选）
  const selectionArray = (
    Array.isArray(selection) ? selection : selection ? [selection] : []
  ) as string[];
  const { checked: isSelected, indeterminate: isIndeterminate } =
    multiple && hasChildren
      ? getNodeCheckState(node, selectionArray)
      : { checked: selectionArray.includes(node.key), indeterminate: false };

  const childrenExpandIcon = isExpanded
    ? (expandIcons?.[0] ?? <ChevronDown className="size-4" />)
    : (expandIcons?.[1] ?? <ChevronRight className="size-4" />);

  const indentPx = level * 24 + 8; // 每级缩进 24 px

  const handleToggleExpand = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleExpand(node.key);
    },
    [onToggleExpand, node.key]
  );

  const handleToggleSelect = useCallback(
    (e?: React.MouseEvent) => {
      if (e) e.stopPropagation();
      const selectable = isNodeSelectable?.(node) ?? true;
      if (!selectable) return;
      onToggleSelect(node.key);
    },
    [onToggleSelect, node, isNodeSelectable]
  );

  const handleNodeClick = useCallback(
    (e: React.MouseEvent) => {
      onNodeClick?.(node, e);
      // 如果节点不可选且有子节点，则切换展开状态
      const selectable = isNodeSelectable?.(node) ?? true;
      if (!selectable && hasChildren) {
        onToggleExpand(node.key);
        return;
      }
      if (!showCheckbox) {
        handleToggleSelect(e);
      }
    },
    [
      onNodeClick,
      node,
      showCheckbox,
      handleToggleSelect,
      isNodeSelectable,
      hasChildren,
      onToggleExpand,
    ]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const selectable = isNodeSelectable?.(node) ?? true;
        if (e.key === 'Enter' && hasChildren) {
          handleToggleExpand(e as any);
        } else if (selectable) {
          // 只有可选节点才能通过键盘选中
          handleToggleSelect();
        } else if (hasChildren) {
          // 不可选节点有子节点时，展开/收起
          handleToggleExpand(e as any);
        }
      }
    },
    [hasChildren, handleToggleExpand, handleToggleSelect, isNodeSelectable, node]
  );

  return (
    <>
      <div
        className={cn(
          'relative flex items-center gap-2 pr-2 py-1.5 rounded-sm text-sm cursor-default outline-hidden select-none',
          'hover:bg-accent hover:text-accent-foreground',
          'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
          '[&_svg:not([class*="text-"])]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-4',
          className
        )}
        style={{ paddingLeft: `${indentPx}px` }}
        data-selected={isSelected}
        onClick={handleNodeClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="treeitem"
        aria-selected={isSelected}
        aria-expanded={hasChildren ? isExpanded : undefined}
      >
        {/* 展开/收起按钮 */}
        {hasChildren ? (
          <button
            onClick={handleToggleExpand}
            className="w-4 h-4 hover:bg-muted-foreground/20 rounded flex items-center justify-center"
            aria-label={isExpanded ? '收起' : '展开'}
          >
            {childrenExpandIcon}
          </button>
        ) : null}

        {/* 复选框 */}
        {showCheckbox && (
          <div className="w-4 h-4 flex items-center justify-center">
            <div
              className={cn(
                'w-4 h-4 border border-input rounded-[4px] shadow-xs flex items-center justify-center',
                isSelected && 'bg-primary border-primary',
                isIndeterminate && !isSelected && 'bg-primary/20 border-primary'
              )}
              onClick={(e) => {
                e.stopPropagation();
                const selectable = isNodeSelectable?.(node) ?? true;
                if (selectable) {
                  handleToggleSelect();
                }
              }}
            >
              {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
              {isIndeterminate && !isSelected && <div className="w-2 h-0.5 bg-primary rounded" />}
            </div>
          </div>
        )}

        {/* 节点内容 */}
        <div className="flex-1 min-w-0 select-none flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            {renderNodeItem ? (
              renderNodeItem({
                node,
                level,
                selected: isSelected,
                expanded: isExpanded,
              })
            ) : (
              <div className="text-sm truncate" title={node.name}>
                {node.name}
              </div>
            )}
          </div>

          {/* 选中图标区域 - 当 showSelectedIcon 为 true 时始终预留位置 */}
          {showSelectedIcon && !showCheckbox && (
            <div className="w-4 h-4 flex items-center justify-center">
              {isSelected && <Check className="w-4 h-4" />}
            </div>
          )}
        </div>
      </div>

      {/* 子节点 */}
      {hasChildren && isExpanded && (
        <div role="group">
          {node.children!.map((child) => (
            <TreeNode<TData, TMultiple>
              key={child.key}
              node={child}
              level={level + 1}
              selection={selection}
              expandedKeys={expandedKeys}
              multiple={multiple}
              showCheckbox={showCheckbox}
              showSelectedIcon={showSelectedIcon}
              onToggleExpand={onToggleExpand}
              onToggleSelect={onToggleSelect}
              onNodeClick={onNodeClick}
              renderNodeItem={renderNodeItem}
              className={className}
              expandIcons={expandIcons}
              isNodeSelectable={isNodeSelectable}
            />
          ))}
        </div>
      )}
    </>
  );
}

function getInitExpandedKeys<TData = unknown>(
  defaultExpandedKeys: string[] | undefined,
  defaultExpandAll: boolean | undefined,
  selection: string[] | string | null | undefined,
  data: TreeNodeData<TData>[]
) {
  if (defaultExpandedKeys) return defaultExpandedKeys;
  if (defaultExpandAll) return getAllKeys(data);
  if (selection && !isEmpty(selection)) return getExpandedKeysForSelection(selection, data);
  // 如果根节点数量较少，默认展开第一个有子节点的根节点
  if (data.length < 4) {
    // 找到第一个有子节点的根节点，并且子节点不会特别多
    const firstExpandedNode = data.find(
      (node) => node.children && node.children.length > 0 && node.children.length < 10
    );
    if (firstExpandedNode) {
      return [firstExpandedNode.key];
    }
  }
  return [];
}

// 主 Tree 组件
function TreeComponent<TData = unknown, TMultiple extends boolean = false>(
  {
    nodeClassName,
    expandIcons,
    data,
    multiple,
    selection,
    onSelectionChange,
    expandedKeys: controlledExpandedKeys,
    onExpandedChange,
    showCheckbox,
    showSelectedIcon = false,
    defaultExpandAll = false,
    defaultExpandedKeys,
    defaultSelection,
    className,
    onNodeClick,
    renderNodeItem,
    emptyText = '暂无数据',
    isNodeSelectable,
  }: TreeProps<TData, TMultiple>,
  ref: React.Ref<TreeRef>
) {
  const initExpandedRef = React.useRef<string[] | undefined>(undefined);
  // 展开状态管理
  const [expandedKeys, setExpandedKeys] = useControl<string[]>(
    () => getInitExpandedKeys(defaultExpandedKeys, defaultExpandAll, selection, data),
    controlledExpandedKeys,
    onExpandedChange
  );

  // 记录首次的 expandedKeys
  if (initExpandedRef.current === undefined) {
    initExpandedRef.current = expandedKeys;
  }

  // 选择状态管理
  const [currentSelection, setCurrentSelection] = useControl<
    TMultiple extends true ? string[] : string | null
  >(
    () => {
      if (defaultSelection !== undefined) {
        return defaultSelection as TMultiple extends true ? string[] : string | null;
      }
      return (multiple ? [] : null) as TMultiple extends true ? string[] : string | null;
    },
    selection,
    onSelectionChange as any // 临时使用 any 来解决类型冲突
  );

  // 确定是否显示复选框
  const shouldShowCheckbox = useMemo(() => {
    return !!(showCheckbox || multiple);
  }, [showCheckbox, multiple]);

  // 暴露给外部的方法
  useImperativeHandle(
    ref,
    () => ({
      setExpandedKeys: (keys: string[]) => {
        setExpandedKeys(keys);
      },
      getExpandedKeys: () => {
        return expandedKeys || [];
      },
      expand: (key: string) => {
        if (!expandedKeys?.includes(key)) {
          setExpandedKeys([...(expandedKeys || []), key]);
        }
      },
      collapse: (key: string) => {
        if (expandedKeys?.includes(key)) {
          setExpandedKeys((expandedKeys || []).filter((k) => k !== key));
        }
      },
      expandAll: () => {
        setExpandedKeys(getAllKeys(data));
      },
      collapseAll: () => {
        setExpandedKeys([]);
      },
      resetExpand: () => {
        setExpandedKeys(initExpandedRef.current || []);
      },
    }),
    [expandedKeys, setExpandedKeys, data]
  );

  // 处理展开状态变化
  const handleToggleExpand = useCallback(
    (key: string) => {
      const currentExpandedKeys = expandedKeys || [];
      const newExpandedKeys = currentExpandedKeys.includes(key)
        ? currentExpandedKeys.filter((k) => k !== key)
        : [...currentExpandedKeys, key];

      setExpandedKeys(newExpandedKeys);
    },
    [expandedKeys, setExpandedKeys]
  );

  // 处理选择状态变化
  const handleToggleSelect = useCallback(
    (key: string) => {
      if (multiple) {
        const currentArray = (Array.isArray(currentSelection) ? currentSelection : []) as string[];

        // 查找节点
        const findNode = (
          nodes: TreeNodeData<TData>[],
          targetKey: string
        ): TreeNodeData<TData> | null => {
          for (const node of nodes) {
            if (node.key === targetKey) return node;
            if (node.children) {
              const found = findNode(node.children, targetKey);
              if (found) return found;
            }
          }
          return null;
        };

        const node = findNode(data, key);
        if (!node) return;

        // 如果有子节点，切换所有叶子节点的选中状态
        if (node.children && node.children.length > 0) {
          const leafKeys = getAllLeafKeys(node);
          const allSelected = leafKeys.every((k) => currentArray.includes(k));

          if (allSelected) {
            // 全部选中 -> 全部取消
            const newSelection = currentArray.filter((k) => !leafKeys.includes(k));
            setCurrentSelection(newSelection as any);
          } else {
            // 部分或未选中 -> 全部选中
            const newSelection = Array.from(new Set([...currentArray, ...leafKeys]));
            setCurrentSelection(newSelection as any);
          }
        } else {
          // 叶子节点，直接切换
          const newSelection = currentArray.includes(key)
            ? currentArray.filter((k) => k !== key)
            : [...currentArray, key];
          setCurrentSelection(newSelection as any);
        }
      } else {
        const newSelection = currentSelection === key ? undefined : key;
        setCurrentSelection(newSelection as any);
      }
    },
    [multiple, currentSelection, setCurrentSelection, data]
  );

  if (data.length === 0) {
    return (
      <div className={className}>
        <div className="py-6 text-center text-sm text-muted-foreground">{emptyText}</div>
      </div>
    );
  }

  return (
    <div className={className} role="tree" aria-multiselectable={multiple}>
      {data.map((node) => (
        <TreeNode<TData, TMultiple>
          key={node.key}
          node={node}
          level={0}
          selection={currentSelection as TMultiple extends true ? string[] : string | null}
          expandedKeys={expandedKeys || []}
          multiple={multiple as TMultiple}
          showCheckbox={shouldShowCheckbox}
          showSelectedIcon={showSelectedIcon}
          className={nodeClassName}
          expandIcons={expandIcons}
          onToggleExpand={handleToggleExpand}
          onToggleSelect={handleToggleSelect}
          onNodeClick={onNodeClick}
          renderNodeItem={renderNodeItem}
          isNodeSelectable={isNodeSelectable}
        />
      ))}
    </div>
  );
}

// 使用 forwardRef 包装组件
export const Tree = forwardRef(TreeComponent) as <
  TData = unknown,
  TMultiple extends boolean = false,
>(
  props: TreeProps<TData, TMultiple> & { ref?: React.Ref<TreeRef> }
) => React.ReactElement | null;

// 导出别名
export { TreeNode as DefaultTreeNode };
export default Tree;
